import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Autocomplete, Chip,
  CircularProgress, Alert, Divider, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { createTeam, updateTeam, getTeam, getRosterMembers, getOutcomesList, getApplicationsList, addMembers, archiveMember, setMemberType } from './services/capacityApi';

export default function TeamForm({ teamId, onSaved, onCancel, isReadOnly }) {
  const isEdit = Boolean(teamId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [historicalRate, setHistoricalRate] = useState('1.0');
  const [historicalRateQa, setHistoricalRateQa] = useState('1.0');
  const [selectedMembers, setSelectedMembers] = useState([]); // [{id, name, role}]
  const [memberTypes, setMemberTypes] = useState({}); // {[rosterId]: "dev"|"qa"}
  const [originalMemberTypes, setOriginalMemberTypes] = useState({});
  const [selectedOutcomes, setSelectedOutcomes] = useState([]); // [{id, name}]
  const [selectedApplications, setSelectedApplications] = useState([]); // [{id, name}]

  const [rosterOptions, setRosterOptions] = useState([]);
  const [outcomeOptions, setOutcomeOptions] = useState([]);
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Original members for edit mode (to detect archival)
  const [originalMembers, setOriginalMembers] = useState([]);

  useEffect(() => {
    async function load() {
      setOptionsLoading(true);
      try {
        // In edit mode: load team text fields first (fast — local JSON),
        // then load autocomplete options (may be slow — S3).
        // This lets the form appear immediately with text fields populated
        // while the autocomplete widgets show inline loading state.
        let teamData = null;
        if (isEdit) {
          teamData = await getTeam(teamId);
          setName(teamData.name || '');
          setDescription(teamData.description || '');
          setHistoricalRate(String(teamData.historical_sp_per_devday ?? '1.0'));
          setHistoricalRateQa(String(teamData.historical_sp_per_qaday ?? '1.0'));
          // selectedMembers/outcomes/apps are set below once options arrive
          // Build member type map from team data
          const typeMap = {};
          for (const m of teamData.members || []) {
            typeMap[m.roster_member_id] = m.member_type || 'dev';
          }
          setMemberTypes(typeMap);
          setOriginalMemberTypes(typeMap);
        }

        const [rRes, oRes, aRes] = await Promise.all([
          getRosterMembers(), getOutcomesList(), getApplicationsList(),
        ]);
        const roster = (rRes?.members || []).sort((a, b) => a.name.localeCompare(b.name));
        setRosterOptions(roster);
        setOutcomeOptions(oRes?.outcomes || []);
        setApplicationOptions(aRes?.applications || []);

        if (isEdit && teamData) {
          const activeIds = (teamData.members || [])
            .filter((m) => !m.archived)
            .map((m) => m.roster_member_id);
          const activeMembers = roster.filter((r) => activeIds.includes(r.id));
          setSelectedMembers(activeMembers);
          setOriginalMembers(activeMembers);
          setSelectedOutcomes((oRes?.outcomes || []).filter((o) => (teamData.outcome_ids || []).includes(o.id)));
          setSelectedApplications((aRes?.applications || []).filter((a) => (teamData.application_ids || []).includes(a.id)));
        }
      } catch (e) {
        setError('Failed to load data');
      } finally {
        setOptionsLoading(false);
      }
    }
    load();
  }, [teamId, isEdit]);

  async function handleSave() {
    if (!name.trim()) { setError('Team name is required'); return; }
    const rate = parseFloat(historicalRate);
    if (isNaN(rate) || rate <= 0) { setError('Historical SP/DevDay must be a positive number'); return; }
    const rateQa = parseFloat(historicalRateQa);
    if (isNaN(rateQa) || rateQa <= 0) { setError('Historical SP/QA Day must be a positive number'); return; }

    setSaving(true);
    setError(null);
    try {
      let team;
      if (isEdit) {
        // Update base fields
        team = await updateTeam(teamId, {
          name: name.trim(),
          description,
          historical_sp_per_devday: rate,
          historical_sp_per_qaday: rateQa,
          outcome_ids: selectedOutcomes.map((o) => o.id),
          application_ids: selectedApplications.map((a) => a.id),
        });

        // Detect added/removed members
        const origIds = new Set(originalMembers.map((m) => m.id));
        const newIds = new Set(selectedMembers.map((m) => m.id));
        const toAdd = selectedMembers.filter((m) => !origIds.has(m.id)).map((m) => m.id);
        const toArchive = originalMembers.filter((m) => !newIds.has(m.id)).map((m) => m.id);

        if (toAdd.length > 0) await addMembers(teamId, { roster_member_ids: toAdd });
        for (const mid of toArchive) await archiveMember(teamId, mid);

        // Apply member type changes (includes newly added members and changed types)
        for (const m of selectedMembers) {
          const currentType = memberTypes[m.id] || 'dev';
          if (originalMemberTypes[m.id] !== currentType || toAdd.includes(m.id)) {
            await setMemberType(teamId, m.id, currentType);
          }
        }
      } else {
        team = await createTeam({
          name: name.trim(),
          description,
          historical_sp_per_devday: rate,
          historical_sp_per_qaday: rateQa,
          outcome_ids: selectedOutcomes.map((o) => o.id),
          application_ids: selectedApplications.map((a) => a.id),
          member_ids: selectedMembers.map((m) => m.id),
          member_types: Object.fromEntries(selectedMembers.map((m) => [m.id, memberTypes[m.id] || 'dev'])),
        });
      }
      onSaved(team);
    } catch (e) {
      setError(e?.message || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  }


  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onCancel} size="small">Teams</Button>
        <Typography variant="h6">{isEdit ? 'Edit Team' : 'New Team'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Team Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          disabled={isReadOnly}
          size="small"
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          disabled={isReadOnly}
          size="small"
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Historical SP / Dev Day"
            value={historicalRate}
            onChange={(e) => setHistoricalRate(e.target.value)}
            type="number"
            inputProps={{ min: 0.01, step: 0.1 }}
            required
            fullWidth
            disabled={isReadOnly}
            size="small"
            helperText="Dev baseline SP/day (until sprint data is available)"
          />
          <TextField
            label="Historical SP / QA Day"
            value={historicalRateQa}
            onChange={(e) => setHistoricalRateQa(e.target.value)}
            type="number"
            inputProps={{ min: 0.01, step: 0.1 }}
            required
            fullWidth
            disabled={isReadOnly}
            size="small"
            helperText="QA baseline SP/day (until sprint data is available)"
          />
        </Box>

        <Divider />

        <Autocomplete
          multiple
          options={rosterOptions}
          getOptionLabel={(o) => `${o.name}${o.role ? ` — ${o.role}` : ''}`}
          value={selectedMembers}
          onChange={(_, v) => setSelectedMembers(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={optionsLoading}
          disabled={isReadOnly || optionsLoading}
          renderInput={(params) => (
            <TextField {...params} label="Team Members" size="small" placeholder="Search roster..." />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
        />

        {selectedMembers.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Member Roles</Typography>
            {selectedMembers.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">{m.name}</Typography>
                <ToggleButtonGroup
                  value={memberTypes[m.id] || 'dev'}
                  exclusive
                  onChange={(_, val) => val && setMemberTypes((prev) => ({ ...prev, [m.id]: val }))}
                  size="small"
                  disabled={isReadOnly}
                >
                  <ToggleButton value="dev" sx={{ py: 0.25, px: 1.5, fontSize: '0.7rem' }}>Dev</ToggleButton>
                  <ToggleButton value="qa" sx={{ py: 0.25, px: 1.5, fontSize: '0.7rem' }}>QA</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            ))}
          </Box>
        )}

        <Autocomplete
          multiple
          options={outcomeOptions}
          getOptionLabel={(o) => o.name}
          value={selectedOutcomes}
          onChange={(_, v) => setSelectedOutcomes(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={optionsLoading}
          disabled={isReadOnly || optionsLoading}
          renderInput={(params) => (
            <TextField {...params} label="Linked Outcomes" size="small" placeholder="Search outcomes..." />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                size="small"
                color="secondary"
                variant="outlined"
                {...getTagProps({ index })}
              />
            ))
          }
        />

        <Autocomplete
          multiple
          options={applicationOptions}
          getOptionLabel={(a) => a.name}
          value={selectedApplications}
          onChange={(_, v) => setSelectedApplications(v)}
          isOptionEqualToValue={(a, v) => a.id === v.id}
          loading={optionsLoading}
          disabled={isReadOnly || optionsLoading}
          renderInput={(params) => (
            <TextField {...params} label="Linked Applications" size="small" placeholder="Search applications..." />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                size="small"
                color="primary"
                variant="outlined"
                {...getTagProps({ index })}
              />
            ))
          }
        />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
          <Button variant="outlined" onClick={onCancel} disabled={saving}>Cancel</Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={saving || optionsLoading}>
              {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {isEdit ? 'Save Changes' : 'Create Team'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
